import type { TranslationKey } from "@/features/translations/types";
import type {
    Brand,
    DownloadDocument,
    Enum,
    ImageType,
    LooseAutocomplete,
    Nullable,
    ResponsiveImage,
    SmartNumberDescriptorKey,
} from "@/types";

export const ConfigurationTab = {
    Design: "design",
    Light: "light",
    Accessory: "accessory",
    Summary: "summary",
} as const;
export type ConfigurationTabEnum = Enum<typeof ConfigurationTab>;

export const FilterType = {
    Radio: "radio",
    RadioImages: "radio-images",
    SingleSelectCheckboxes: "single-select-checkboxes",
} as const;
export type FilterTypeEnum = Enum<typeof FilterType>;

export const Difference = {
    Change: "change",
    Remove: "Remove",
} as const;
export type DifferenceEnum = Enum<typeof Difference>;

export const ProductImageSubType = {
    Drawing: "drawing",
} as const;
export type ProductImageSubTypeEnum = Enum<typeof ProductImageSubType>;

// Important distinctions
export type SubFamilyId = Brand<string, "subfamily-id">;
export type SmartNumber = Brand<string, "smart-number">;
export type ModelUrl = Brand<string, "model-url">;
export type DynamicSpecSheet = Brand<string, "dynamic-spec-sheet">;
export type ZipDownloadPath = Brand<string, "zip-download-path">;
export type ShortCode = Brand<string, "short-code">;

// Smart number parts
export type ModelCode = Brand<string, "smart-number__model-code">;
export type TypeNumber = Brand<string, "smart-number__type-number">;
export type PropertyConfigCode = Brand<string, "smart-number__property-config-code">;
export type ProductId = Brand<string, "product-id">;
export type MassCode = Brand<string, "mass-code">;
export type ProductReference = Brand<string, "product-reference">;

// Installation
export type InstallationId = Brand<string, "installation-id">;

export type InstallationType = {
    id: InstallationId;
    name: string;
    code: Nullable<string>;
};

export type SubFamily = {
    glb_url: ModelUrl;
    id: SubFamilyId;
    image: Nullable<ResponsiveImage>;
    installationTypes: InstallationType[];
    modelCode: ModelCode;
    modelName: string;
    name: string;
    smartNumber: SmartNumber;
    smartNumberMapping: Nullable<unknown>; // TODO: ?
    technicalDrawing: Nullable<ResponsiveImage>;
    typeNumber: TypeNumber;
};

export type Property<V = string[]> = {
    configCode: PropertyConfigCode;
    label: Nullable<string>;
    name: string;
    originalValue: Nullable<string | string[]>;
    sortkey: Nullable<string>;
    temperature: Nullable<string>;
    type: string; // Doesn't matter in this context
    uid: string;
    unit: Nullable<string>;
    value: Nullable<V>;
    view: Nullable<ResponsiveImage>;
    workingTitle: Nullable<string>;
};

export type BaseProperty<V = string[]> = Pick<Property<V>, "value" | "unit" | "name">;

export type ProductImage = { subType: ProductImageSubTypeEnum; url: string };
export type MountingKit = { ceilingLabel: string; configCode: PropertyConfigCode; sku: string; description: Nullable<string> }; // prettier-ignore
export type LightAccessory = { title: string; code: PropertyConfigCode; asset: Nullable<ResponsiveImage>; description: Nullable<string>; sku: string }; // prettier-ignore
export type Supply = { product_label: string; title: string; code: PropertyConfigCode; sku: string };

export type Properties = {
    // Select boxes
    beam_angle: Property;
    cct: Property;
    cri: Property;
    dimension: Property;
    dimming_method: Property;
    dimming_type: Property;
    ledchip: Property;
    light_distributor: Property;
    power_supply: Property;
    cct_type: Property;
    convector: Property; // Actually converter, but no-one dares to rename this property 😅
    fixation: Property;
    // Others
    documents: Property<DownloadDocument[]>;
    images: Property<ProductImage[]>;
    ceiling_meta_data: MountingKit[];
    light_acc: LightAccessory[];
    supply: Supply[];
    led_technics_light_source: Property;
    led_technics_luminaire: Property;
};

export type ProductPropertyKey = keyof Properties;

export type Product = {
    configuratorCode: Nullable<string>; // TODO
    id: ProductId;
    massCode: MassCode;
    properties: Properties;
    sku: string;
    title: string;
    shortcode: Nullable<ShortCode>;
};
export type ProductData = Record<ProductReference, Product>;

export type Choice = {
    value: string;
    label: LooseAutocomplete<TranslationKey>;
    description: Nullable<string>;
    image: Nullable<ImageType>;
    configCode: PropertyConfigCode | null;
    sortKey: Nullable<string | number>;
    tooltip: Nullable<string>;
};

export type ChoiceWithPossibleIndicator = Choice & { isPossible: boolean };

export type FilterOption = {
    label: LooseAutocomplete<TranslationKey>;
    tab: Nullable<ConfigurationTabEnum>;
    filterType: FilterTypeEnum;
    choices: Choice[];
    descriptor: SmartNumberDescriptorKey;
    sortKey: Nullable<string | number>;
};
export type FilterOptions = Record<ProductPropertyKey, FilterOption>;

export type SmartNumberMapping = Record<SmartNumberDescriptorKey, PropertyConfigCode>;

// This has other properties too, but are not relevant yet
export type SmartNumberDescriptionDefinition = {
    CharacteristicName: SmartNumberDescriptorKey;
    CharacteristicLength: string;
};
export type SmartNumberDescription = SmartNumberDescriptionDefinition[];

export type Colour = {
    assetView: Nullable<ResponsiveImage>;
    code: PropertyConfigCode;
    colourId: string;
    id: string;
    isDefault: boolean;
    name: string;
    pimcoreId: string;
};

export type PartId = Brand<string, "part-id">;
export type PartNo = Brand<string, "part-no">;
export type Part = {
    colors: Colour[];
    followsPartNo: string;
    id: string;
    label: string;
    partId: PartId;
    partNo: PartNo;
    assetView: Nullable<ResponsiveImage>;
};

export type AllowedCombinationKey = Extract<
    ProductPropertyKey,
    "cct" | "cri" | "beam_angle" | "dimming_type" | "supply"
>;
export type Combination = Record<AllowedCombinationKey, PropertyConfigCode> & { massCode: MassCode };
export type CombinationKey = keyof Combination;

export type ChangeValue = { descriptor: SmartNumberDescriptorKey; configCode: PropertyConfigCode };
export type Differences = Array<{ property: ProductPropertyKey | CombinationKey; difference: DifferenceEnum }>;

type OptionKey = "accessory" | "mountingKit" | "powerSupply";
export type CurrentOptions = Record<OptionKey, Nullable<PropertyConfigCode>>;
